import { getColorClass, getBackgroundColorClass } from "@/lib/colors";
import type { TriggerEventContext, TriggerRenderer, TriggerRendererContext } from "../types";
import gitlabIcon from "@/assets/icons/integrations/gitlab.svg";
import type { TriggerProps } from "@/ui/trigger";
import type { GitLabNodeMetadata } from "./types";
import { buildGitlabSubtitle } from "./utils";
import type { WebhookIssue } from "./issue_utils";
import { getDetailsForWebhookIssue } from "./issue_utils";
import type { Predicate } from "../eventDisplay";
import { formatPredicate } from "../eventDisplay";

interface OnIssueConfiguration {
  actions: string[];
  project: string;
  labels: Predicate[];
}

interface OnIssueEventData {
  object_kind?: string;
  event_type?: string;
  object_attributes?: WebhookIssue;
  user?: {
    id: number;
    name: string;
    username: string;
  };
  project?: {
    id: number;
    name: string;
    path_with_namespace: string;
    web_url: string;
  };
}

export const onIssueTriggerRenderer: TriggerRenderer = {
  getTitleAndSubtitle: (context: TriggerEventContext) => {
    const eventData = context.event?.data as OnIssueEventData;
    const issue = eventData?.object_attributes;

    return {
      title: `#${issue?.iid ?? ""} - ${issue?.title || "Issue"}`,
      subtitle: buildGitlabSubtitle(issue?.action || "", context.event?.createdAt),
    };
  },

  getRootEventValues: (context: TriggerEventContext): Record<string, string> => {
    const eventData = context.event?.data as OnIssueEventData;
    const issue = eventData?.object_attributes;
    const values = getDetailsForWebhookIssue(issue);

    if (eventData?.user?.username) {
      values["Author"] = eventData.user.username;
    }

    if (eventData?.project?.path_with_namespace) {
      values["Project"] = eventData.project.path_with_namespace;
    }

    return values;
  },

  getTriggerProps: (context: TriggerRendererContext): TriggerProps => {
    const { node, definition, lastEvent } = context;
    const metadata = node.metadata as unknown as GitLabNodeMetadata;
    const configuration = node.configuration as unknown as OnIssueConfiguration;
    const metadataItems = [];

    if (metadata?.project?.name) {
      metadataItems.push({
        icon: "book",
        label: metadata.project.name,
      });
    }

    if (configuration?.actions) {
      metadataItems.push({
        icon: "funnel",
        label: configuration.actions.join(", "),
      });
    }

    if (configuration?.labels && configuration?.labels?.length > 0) {
      metadataItems.push({
        icon: "label",
        label: configuration.labels.map((label) => formatPredicate(label)).join(", "),
      });
    }

    const props: TriggerProps = {
      title: node.name || definition.label || "Unnamed trigger",
      iconSrc: gitlabIcon,
      iconColor: getColorClass(definition.color),
      collapsedBackground: getBackgroundColorClass(definition.color),
      metadata: metadataItems,
    };

    if (lastEvent) {
      const eventData = lastEvent.data as OnIssueEventData;
      const issue = eventData?.object_attributes;

      props.lastEventData = {
        title: `#${issue?.iid ?? ""} - ${issue?.title || "Issue"}`,
        subtitle: buildGitlabSubtitle(issue?.action || "", lastEvent.createdAt),
        receivedAt: new Date(lastEvent.createdAt!),
        state: "triggered",
        eventId: lastEvent.id!,
      };
    }

    return props;
  },
};
